import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import { BackLink, Spinner } from '../components/ui';
import { api, ApiError, ensureSession, useMe } from '../lib/api';

interface Friend {
  id: string;
  displayName: string;
}

async function shareOrCopy(url: string): Promise<boolean> {
  try {
    if (navigator.share) {
      await navigator.share({ url });
      return false;
    }
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

export function Friends() {
  const { t, i18n } = useTranslation();
  const [friends, setFriends] = useState<Friend[] | null>(null);
  const [inbox, setInbox] = useState<
    { id: string; mode: string; from_name: string; expires_at: string }[]
  >([]);
  const [invite, setInvite] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const load = useCallback(async () => {
    await ensureSession();
    const [f, i] = await Promise.all([
      api<{ friends: Friend[] }>('GET', '/api/friends'),
      api<{ challenges: typeof inbox }>('GET', '/api/challenges/inbox'),
    ]);
    setFriends(f.friends);
    setInbox(i.challenges);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  if (!friends) return <Spinner label={t('common.loading')} />;
  return (
    <main className="page">
      <BackLink to="/me" />
      <h1>{t('friends.title')}</h1>
      {inbox.length > 0 && (
        <section className="card">
          <h2>{t('friends.inbox')}</h2>
          {inbox.map((c) => (
            <div key={c.id} className="row round-row">
              <span>
                {c.from_name} · {t(`modes.${c.mode}.name`)}
              </span>
              <span className="muted small">
                {new Date(c.expires_at).toLocaleDateString(i18n.language)}
              </span>
            </div>
          ))}
          <p className="muted small">{t('friends.inboxNote')}</p>
        </section>
      )}
      <section className="card">
        <button
          className="btn primary"
          onClick={async () => {
            const r = await api<{ inviteUrl: string }>('POST', '/api/friends/invite', {});
            setInvite(r.inviteUrl);
            setCopied(await shareOrCopy(r.inviteUrl));
          }}
        >
          {t('friends.invite')}
        </button>
        {invite && (
          <p className="invite-url">
            <code>{invite}</code>{' '}
            {copied && <span className="muted small">{t('common.copied')}</span>}
          </p>
        )}
        <p className="muted small">{t('friends.inviteNote')}</p>
      </section>
      {friends.length === 0 ? (
        <p className="muted">{t('friends.none')}</p>
      ) : (
        <table className="data">
          <tbody>
            {friends.map((f) => (
              <tr key={f.id}>
                <td>{f.displayName}</td>
                <td className="num">
                  <Link className="btn small primary" href={`/challenge/new?to=${f.id}`}>
                    {t('friends.challenge')}
                  </Link>{' '}
                  <button
                    className="btn small ghost"
                    onClick={async () => {
                      await api('DELETE', `/api/friends/${f.id}`);
                      await load();
                    }}
                  >
                    {t('friends.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}

export function FriendAccept({ params }: { params: { token: string } }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ensureSession()
      .then(() => api('POST', '/api/friends/accept', { token: params.token }))
      .then(() => navigate('/friends', { replace: true }))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError && e.code === 'own_invite'
            ? t('friends.ownInvite')
            : t('friends.inviteInvalid'),
        ),
      );
  }, [params.token, navigate, t]);
  return error ? (
    <main className="page">
      <p className="message error">{error}</p>
    </main>
  ) : (
    <Spinner label={t('common.loading')} />
  );
}

interface TeamView {
  team: {
    id: string;
    name: string;
    members: string[];
    weekly: { week: string; words: number; goal: number; members: number };
  } | null;
}

export function Team() {
  const { t } = useTranslation();
  const { user } = useMe();
  const [data, setData] = useState<TeamView | null>(null);
  const [name, setName] = useState('');
  const [invite, setInvite] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    await ensureSession();
    setData(await api<TeamView>('GET', '/api/teams/mine'));
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  if (!data) return <Spinner label={t('common.loading')} />;
  if (!data.team) {
    return (
      <main className="page">
        <BackLink to="/me" />
        <h1>{t('team.title')}</h1>
        <p className="muted">{t('team.intro')}</p>
        {user?.kind !== 'registered' ? (
          <Link className="btn primary" href="/account">
            {t('online.saveAccount')}
          </Link>
        ) : (
          <form
            className="card"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await api<{ inviteUrl: string }>('POST', '/api/teams', { name });
                setInvite(r.inviteUrl);
                await load();
              } catch (err) {
                setError(
                  err instanceof ApiError
                    ? t(`account.errors.${err.code}`, { defaultValue: t('errors.generic') })
                    : t('errors.generic'),
                );
              }
            }}
          >
            <label className="field">
              <span>{t('team.name')}</span>
              <input
                type="text"
                value={name}
                minLength={3}
                maxLength={24}
                required
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            {error && <p className="message error">{error}</p>}
            <button className="btn primary" type="submit">
              {t('team.create')}
            </button>
          </form>
        )}
      </main>
    );
  }
  const w = data.team.weekly;
  const pct = Math.min(100, Math.round((100 * w.words) / w.goal));
  return (
    <main className="page">
      <BackLink to="/me" />
      <h1>{data.team.name}</h1>
      <section className="card">
        <h2>{t('team.weekly', { week: w.week })}</h2>
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
        <p>{t('team.progress', { words: w.words, goal: w.goal })}</p>
        <p className="muted small">{t('team.rewardNote')}</p>
      </section>
      <section className="card">
        <h2>{t('team.members', { n: data.team.members.length })}</h2>
        <ul>
          {data.team.members.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
        {invite && (
          <p className="invite-url">
            <code>{invite}</code>
          </p>
        )}
      </section>
      <button
        className="btn ghost small"
        onClick={async () => {
          await api('DELETE', '/api/teams/mine');
          await load();
        }}
      >
        {t('team.leave')}
      </button>
    </main>
  );
}

export function TeamJoin({ params }: { params: { token: string } }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ensureSession()
      .then(() => api('POST', '/api/teams/join', { token: params.token }))
      .then(() => navigate('/team', { replace: true }))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError && e.status === 403
            ? t('team.accountRequired')
            : t('team.joinFailed'),
        ),
      );
  }, [params.token, navigate, t]);
  return error ? (
    <main className="page">
      <p className="message error">{error}</p>
      <Link href="/account">{t('online.saveAccount')}</Link>
    </main>
  ) : (
    <Spinner label={t('common.loading')} />
  );
}

/** Block / report actions on an opponent (after a game). */
export function OpponentActions({ userId, matchId }: { userId: string; matchId: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState<string | null>(null);
  if (done) return <p className="muted small">{done}</p>;
  return (
    <div className="row wrap">
      <button
        className="btn ghost small"
        onClick={async () => {
          await api('POST', `/api/users/${userId}/block`, {});
          setDone(t('moderation.blocked'));
        }}
      >
        {t('moderation.block')}
      </button>
      <select
        className="small"
        defaultValue=""
        aria-label={t('moderation.report')}
        onChange={async (e) => {
          if (!e.target.value) return;
          await api('POST', `/api/users/${userId}/report`, { reason: e.target.value, matchId });
          setDone(t('moderation.reported'));
        }}
      >
        <option value="">{t('moderation.report')}</option>
        {(['nickname', 'cheating', 'harassment', 'other'] as const).map((r) => (
          <option key={r} value={r}>
            {t(`moderation.reasons.${r}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
